'use client';

import { Box, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { ChangeEvent } from 'react';

import { AbgTypography, FormError, useTranslation } from '@abg/kit-base';
import { RadioCheckedIcon } from '@abg/kit-base/lib/components/radio/RadioCheckedIcon';
import { RadioUncheckedIcon } from '@abg/kit-base/lib/components/radio/RadioUncheckedIcon';

import {
  mfaMethods,
  OtpMethod,
} from '../../../shared/constants/login-form.constants';

import { selectMfaFormBodyStyling as styling } from './SelectMfaFormBody.styling';
import { SelectMfaFormBodyProps } from './types';

const outerCircleSizeSx = {
  width: '16px',
  height: '16px',
};

export const SelectMfaFormBody = ({
  styleParts = {},
  partProps = {},
  model,
  mfaData,
  error,
  selectedMethod,
  setSelectedMethod,
}: SelectMfaFormBodyProps) => {
  const t = useTranslation(model.i18n);

  const handleChangeMethod = (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    setSelectedMethod(event.target.value as OtpMethod);
  };

  return (
    <>
      <AbgTypography
        data-testid="mfa-dialog-select-method-description"
        sx={styling.description}
      >
        {t('mfa.modal.screen.selectMethod.description')}
      </AbgTypography>

      {!!error && (
        <FormError
          data-testid="mfa-dialog-form-server-error"
          styleParts={partProps.formError?.styleParts}
          config={error}
        />
      )}

      <Box>
        <AbgTypography
          sx={styling.title}
          data-testid="mfa-dialog-select-method-title"
        >
          {t('mfa.modal.authentication.options.title')}
        </AbgTypography>
        <RadioGroup
          value={selectedMethod}
          sx={styling.methodSelection.radioGroup}
        >
          {mfaMethods.map((mfaMethod) => {
            const isSelected = selectedMethod === mfaMethod;

            return (
              <Box
                sx={styling.methodSelection.radio}
                onChange={handleChangeMethod}
                key={mfaMethod}
              >
                <FormControlLabel
                  sx={styling.methodSelection.radioWithLabel}
                  control={
                    <Radio
                      data-testid="mfa-dialog-radio-btn"
                      value={mfaMethod}
                      icon={
                        <RadioUncheckedIcon
                          sxOverrides={
                            styling.methodSelection.radioBtnOuterCircle
                          }
                        />
                      }
                      checkedIcon={
                        <RadioCheckedIcon
                          sxOverrides={{
                            innerCircleSx: {
                              ...styling.methodSelection.radioBtnInnerCircle,
                              ...styleParts.radioInnerCircle,
                            } as SxProps,
                            outerCircleSx: (theme: Theme) => ({
                              ...outerCircleSizeSx,
                              border: `1px solid ${theme.palette.secondary.dark}`,
                            }),
                          }}
                        />
                      }
                      sx={styling.methodSelection.radioBtn}
                    />
                  }
                  label={
                    mfaMethod === OtpMethod.Phone ? (
                      <AbgTypography
                        data-testid="mfa-dialog-method-radio-label"
                        sx={styling.methodSelection.methodTitle(isSelected)}
                      >
                        {t('mfa.modal.authentication.option.text.title')}
                      </AbgTypography>
                    ) : (
                      <AbgTypography
                        data-testid="mfa-dialog-method-radio-label"
                        sx={styling.methodSelection.methodTitle(isSelected)}
                      >
                        {t('mfa.modal.authentication.option.email.title')}
                      </AbgTypography>
                    )
                  }
                />
                <AbgTypography
                  data-testid="mfa-dialog-contact-data"
                  sx={styling.methodSelection.contactData(isSelected)}
                >
                  {mfaMethod === OtpMethod.Phone
                    ? mfaData?.phone
                    : mfaData?.email}
                </AbgTypography>
              </Box>
            );
          })}
        </RadioGroup>
      </Box>
    </>
  );
};
